import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminRequest, getAdminHeaders, toAssetUrl } from "./adminApi";
import AdminResetPasswordPanel from "./AdminResetPasswordPanel";

export default function AdminConsultantDetail() {
  const { consultantId } = useParams();
  const navigate = useNavigate();

  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsultant();
  }, [consultantId]);

  const fetchConsultant = async () => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultantId}`,
        {
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "获取咨询师资料失败");
        navigate("/admin/consultants");
        return;
      }

      setConsultant(data);
    } catch (err) {
      console.error(err);
      alert("获取咨询师资料失败，请确认后端已启动");
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailable = async () => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultantId}/toggle-available`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchConsultant();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const auditConsultant = async (approved) => {
    const rejectReason = approved ? "" : window.prompt("请输入不通过原因", "");

    if (!approved && rejectReason === null) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultantId}/audit`,
        {
          method: "POST",
          headers: getAdminHeaders(true),
          body: JSON.stringify({
            approved,
            reason: rejectReason || "",
          }),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "审核失败");
        return;
      }

      alert(approved ? "已认证通过" : "已取消认证");
      fetchConsultant();
    } catch (err) {
      console.error(err);
      alert("审核失败");
    }
  };

  const deleteConsultant = async () => {
    const name = consultant.name || consultant.phone || `ID ${consultant.id}`;
    if (
      !window.confirm(
        `确定删除咨询师「${name}」吗？\n此操作不可恢复，关联的订单和消息也会一并删除。`
      )
    ) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/consultants/${consultantId}/delete`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || data?.msg || "删除失败");
        return;
      }

      alert("咨询师已删除");
      navigate("/admin/consultants");
    } catch (err) {
      console.error(err);
      alert("删除失败");
    }
  };

  if (loading) {
    return <div className="text-gray-400">加载中...</div>;
  }

  if (!consultant) {
    return <div className="text-gray-400">咨询师不存在</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate("/admin/consultants")}
        className="mb-6 text-gray-500 hover:text-blue-600"
      >
        返回咨询师列表
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <section className="flex-1 space-y-6">
          <div className="bg-white rounded-2xl shadow p-6">
            <div className="flex items-center gap-4">
              <img
                src={toAssetUrl(consultant.avatar) || "/default-avatar.png"}
                alt={consultant.name}
                className="w-20 h-20 rounded-full object-cover bg-gray-100"
              />

              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {consultant.name || "未填写"}
                </h1>
                <p className="text-gray-400 mt-1">{consultant.phone || "-"}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Badge ok={consultant.is_verified} text="认证" />
                  <Badge ok={consultant.is_online} text="接单" />
                  <Badge ok={consultant.is_available} text="前台展示" />
                </div>
              </div>
            </div>
          </div>

          <InfoBlock title="服务资料">
            <InfoGrid
              items={[
                ["性别", consultant.gender],
                ["年龄", consultant.age],
                ["城市", consultant.city],
                [
                  "从业年限",
                  consultant.experience_years
                    ? `${consultant.experience_years}年`
                    : "",
                ],
                ["价格", consultant.price ? `¥${consultant.price}` : ""],
                [
                  "时长",
                  consultant.duration ? `${consultant.duration}分钟` : "",
                ],
                ["服务方式", consultant.service_types],
                ["擅长方向", consultant.specialties],
              ]}
            />
          </InfoBlock>

          <InfoBlock title="专业介绍">
            <TextLine label="专业背景" value={consultant.background} />
            <TextLine label="咨询风格" value={consultant.consultation_style} />
            <TextLine label="个人简介" value={consultant.intro} />
          </InfoBlock>

          <InfoBlock title="资质证书">
            {Array.isArray(consultant.certificates) &&
            consultant.certificates.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {consultant.certificates.map((url) => (
                  <a
                    key={url}
                    href={toAssetUrl(url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={toAssetUrl(url)}
                      alt="资质证书"
                      className="w-full aspect-[16/10] rounded-xl object-cover bg-gray-100 border"
                    />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">未上传资质证书</p>
            )}
          </InfoBlock>
        </section>

        <aside className="w-full lg:w-80">
          <div className="bg-white rounded-2xl shadow p-6 sticky top-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">审核操作</h2>
            <div className="space-y-3 text-sm text-gray-600 mb-6">
              <InfoRow
                label="认证状态"
                value={consultant.is_verified ? "已认证" : "待认证"}
              />
              <InfoRow
                label="接单状态"
                value={consultant.is_online ? "在线接单" : "暂停接单"}
              />
              <InfoRow
                label="前台展示"
                value={consultant.is_available ? "展示中" : "已隐藏"}
              />
              <InfoRow
                label="注册时间"
                value={formatTime(consultant.created_at)}
              />
            </div>

            <button
              onClick={() => auditConsultant(true)}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
            >
              认证通过
            </button>
            <button
              onClick={() => auditConsultant(false)}
              className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"
            >
              取消认证 / 不通过
            </button>
            <button
              onClick={toggleAvailable}
              className="w-full mt-3 py-3 rounded-xl border border-blue-200 text-blue-600 font-bold hover:bg-blue-50"
            >
              {consultant.is_available ? "隐藏前台展示" : "开启前台展示"}
            </button>
            <button
              onClick={() =>
                navigate("/admin/messages", {
                  state: {
                    recipientType: "consultant",
                    recipientId: consultant.id,
                  },
                })
              }
              className="w-full mt-3 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"
            >
              发送系统消息
            </button>
            <button
              onClick={deleteConsultant}
              className="w-full mt-3 py-3 rounded-xl border border-red-200 text-red-500 font-bold hover:bg-red-50"
            >
              删除咨询师
            </button>

            <AdminResetPasswordPanel
              role="consultant"
              accountId={consultant.id}
              phone={consultant.phone}
              name={consultant.name}
              email={consultant.email}
              emailVerified={Boolean(consultant.email_verified)}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function InfoGrid({ items }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map(([label, value]) => (
        <InfoRow key={label} label={label} value={value} />
      ))}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-gray-700 font-medium mt-1">{value || "未填写"}</p>
    </div>
  );
}

function TextLine({ label, value }) {
  return (
    <div className="mb-4 last:mb-0">
      <p className="text-gray-400 text-sm mb-1">{label}</p>
      <p className="text-gray-700 leading-7 whitespace-pre-wrap">
        {value || "未填写"}
      </p>
    </div>
  );
}

function Badge({ ok, text }) {
  return (
    <span
      className={`text-xs px-2 py-1 rounded-full ${
        ok ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
      }`}
    >
      {ok ? `${text}已通过` : `${text}未通过`}
    </span>
  );
}

function formatTime(time) {
  return time ? new Date(time).toLocaleString() : "未记录";
}
